import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { ReplaySubject, map } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '@environments/environment';

export interface StatusDropdownResponse {
  Return: string[];
  Request: string[];
  Damage: string[];
  Inventory: string[];
}

export type StatusCategory = 'Return' | 'Request' | 'Damage' | 'Inventory';

export interface StatusOption {
  value: string;
  label: string;
  icon: string;
  color: string;
  colorClass: string;
  category: StatusCategory;
}

@Injectable({
  providedIn: 'root'
})
export class StatusApiService {
  private statusDropdownRequest$: Observable<StatusDropdownResponse> | null = null;
  private readonly apiUrl = `${environment.apiUrl}/asset-management/getStatusDropdown`;
  private statusCache$ = new ReplaySubject<StatusDropdownResponse>(1);

  // Icon mappings for different statuses
  private readonly iconMap: Record<string, string> = {
    'Pending': 'pending',
    'Done': 'check_circle',
    'Initiated': 'progress_activity',
    'Cancelled': 'cancel',
    'Ready': 'featured_seasonal_and_gifts',
    'Delivered': 'approval_delegation',
    'Cost Received': 'payment',
    'Exception Received': 'warning'
  };

  // Color class mappings
  private readonly colorClassMap: Record<string, string> = {
    'Pending': 'status-pending',
    'Done': 'status-done',
    'Initiated': 'status-initiated',
    'Cancelled': 'status-cancelled',
    'Ready': 'status-ready',
    'Delivered': 'status-delivered',
    'Cost Received': 'status-cost-received',
    'Exception Received': 'status-exception-received'
  };

  constructor(private http: HttpClient) {}

  /**
   * Fetch status dropdown data from API
   */
  getStatusDropdown(): Observable<StatusDropdownResponse> {
    if (!this.statusDropdownRequest$) {
      this.statusDropdownRequest$ = this.http.get<StatusDropdownResponse>(this.apiUrl).pipe(
        tap(response => {
          this.statusCache$.next(response);
        }),
        catchError(error => {
          console.error('Failed to fetch status dropdown:', error);
          const fallback = this.getFallbackData();
          this.statusCache$.next(fallback);
          this.statusDropdownRequest$ = null;
          return of(fallback);
        })
      );
      this.statusDropdownRequest$.subscribe();
    }
    return this.statusCache$.asObservable();
  }

  /**
   * Get statuses for a specific category
   */
  getStatusesForCategory(category: StatusCategory): Observable<string[]> {
    return this.getStatusDropdown().pipe(
      map((response: StatusDropdownResponse) => response[category] || [])
    );
  }

  /**
   * Get formatted status options for a category
   */
  getStatusOptionsForCategory(category: StatusCategory): Observable<StatusOption[]> {
    return this.getStatusesForCategory(category).pipe(
      map((statuses: string[]) =>
        statuses.map(status => ({
          value: status,
          label: status,
          icon: this.getIconForStatus(status),
          color: this.getStatusColor(status),
          colorClass: this.getColorClassForStatus(status),
          category: category
        }))
      )
    );
  }

  getStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      'pendingrounded': '#D5710C',
      'Done': '#22c55e',
      'Initiated': '#3b82f6',
      'Cancelled': '#ef4444',
      'Ready': '#8b5cf6',
      'Delivered': '#06b6d4',
      'Cost Received': '#489969',
      'Exception Received': '#A03530'
    };
    return colorMap[status] || '#666';
  }

  /**
   * Get icon name for a status
   */
  getIconForStatus(status: string): string {
    return this.iconMap[status] || 'help_outline';
  }

  /**
   * Get color class for a status
   */
  getColorClassForStatus(status: string): string {
    return this.colorClassMap[status] || 'status-default';
  }

  /**
   * Check if a status is valid for a category
   */
  isValidStatusForCategory(status: string, category: StatusCategory): Observable<boolean> {
    return this.getStatusesForCategory(category).pipe(
      map((statuses: string[]) => statuses.includes(status))
    );
  }

  /**
   * Get all categories
   */
  getCategories(): StatusCategory[] {
    return ['Return', 'Request', 'Damage', 'Inventory'];
  }

  /**
   * Fallback data when API fails
   */
  private getFallbackData(): StatusDropdownResponse {
    return {
      Return: ['Pending', 'Done', 'Initiated', 'Cancelled'],
      Request: ['Initiated', 'Pending', 'Ready', 'Delivered', 'Cancelled'],
      Damage: ['Pending', 'Cost Received', 'Exception Received'],
      Inventory: ['Pending', 'Delivered']
    };
  }

  /**
   * Clear cache and refetch data
   */
  refreshStatusData(): Observable<StatusDropdownResponse> {
    this.statusDropdownRequest$ = null;
    this.statusCache$ = new ReplaySubject<StatusDropdownResponse>(1);
    return this.getStatusDropdown();
  }
}
